import type { Phone } from '../types'
import { PhoneRow } from './PhoneRow'
import type { PhoneActionsCallbacks } from './PhoneActions'
import type { SortDir, SortKey, SortState } from '../lib/sortPhones'

interface PhoneTableProps extends PhoneActionsCallbacks {
  phones: Phone[]
  sort: SortState
  onSortChange: (s: SortState) => void
  /** Empty-state copy when phones list is empty. */
  emptyText?: string
}

const TH_BASE =
  'py-2 px-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400'
const TH_SORTABLE = `${TH_BASE} cursor-pointer select-none hover:text-gray-700 dark:hover:text-gray-200`

interface HeaderProps {
  active: boolean
  dir: SortDir
  onClick: () => void
  children: React.ReactNode
  /** Extra Tailwind classes appended to the base TH styling (e.g. `w-full`
   * on the flex-column header). */
  className?: string
}

function Header({ active, dir, onClick, children, className }: HeaderProps) {
  const arrow = !active ? '' : dir === 'asc' ? ' ↑' : ' ↓'
  return (
    <th
      onClick={onClick}
      className={className ? `${TH_SORTABLE} ${className}` : TH_SORTABLE}
    >
      {children}
      {arrow}
    </th>
  )
}

export function PhoneTable({
  phones,
  sort,
  onSortChange,
  emptyText = 'No phones',
  ...actions
}: PhoneTableProps) {
  const toggle = (key: SortKey) => {
    if (sort.key === key) {
      onSortChange({ key, dir: sort.dir === 'asc' ? 'desc' : 'asc' })
    } else {
      onSortChange({ key, dir: 'asc' })
    }
  }

  if (phones.length === 0) {
    return (
      <div className="text-center text-sm text-gray-500 dark:text-gray-400 py-8">
        {emptyText}
      </div>
    )
  }

  return (
    <table className="w-full text-sm">
      <thead className="sticky top-0 bg-gray-50/80 dark:bg-gray-950/80 backdrop-blur">
        <tr className="border-b border-gray-200 dark:border-gray-700">
          <th className="py-2 pl-3 pr-2 w-6" aria-label="Status" />
          {/* `w-full` on this <th> is the standard "flex column" trick for
              HTML tables — the browser distributes any leftover container
              width to this column instead of the trailing actions cell,
              keeping action icons flush to the right edge. */}
          <Header
            active={sort.key === 'name'}
            dir={sort.dir}
            onClick={() => toggle('name')}
            className="w-full"
          >
            Имя
          </Header>
          <th className={TH_BASE}>SIM</th>
          <th className={TH_BASE}>Сеть</th>
          <th className={TH_BASE}>Разрешения</th>
          <Header active={sort.key === 'battery'} dir={sort.dir} onClick={() => toggle('battery')}>
            Батарея
          </Header>
          <th className={TH_BASE}>Оператор</th>
          <th className={TH_BASE}>Аптайм</th>
          <th className={TH_BASE}>Модель</th>
          <th className={TH_BASE}>Android</th>
          <th className={TH_BASE}>Версия</th>
          <th className={`${TH_BASE} pl-2 pr-3`}>Действия</th>
        </tr>
      </thead>
      <tbody>
        {phones.map(p => <PhoneRow key={p.id} phone={p} {...actions} />)}
      </tbody>
    </table>
  )
}

